import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { submissionApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { formatDate, formatFileSize, getRiskColor, getStatusColor } from '../lib/utils';
import { 
  FileText, 
  Download, 
  Mail, 
  ArrowLeft, 
  Clock, 
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  Loader2,
  RefreshCw,
  Globe,
  Database,
  BookOpen,
  Layers
} from 'lucide-react';

const SubmissionDetail = () => {
  const { id } = useParams();
  const [submission, setSubmission] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [customEmail, setCustomEmail] = useState('');

  const fetchData = async () => {
    try {
      const [submissionRes, resultsRes] = await Promise.all([
        submissionApi.get(id),
        submissionApi.getResults(id).catch(() => ({ data: [] }))
      ]);
      setSubmission(submissionRes.data);
      setResults(resultsRes.data);
    } catch (error) {
      console.error('Failed to fetch submission:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    // Poll for updates if processing
    const interval = setInterval(() => {
      if (submission?.status === 'processing' || submission?.status === 'pending') {
        fetchData();
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [id, submission?.status]);

  const handleDownload = (format = 'pdf') => {
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    
    // Direct download URL with token
    const downloadUrl = `${apiUrl}/api/submissions/${id}/report?format=${format}&token=${token}`;
    
    console.log('Download URL:', downloadUrl);
    
    // Create a temporary link and click it
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `RBA_Similarity_Report_${id.slice(0, 8)}.${format}`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    
    // Add to DOM, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Show help message
    alert(`Opening PDF download...\n\nIf the PDF opens in a new tab instead of downloading:\n1. Right-click on the PDF\n2. Select "Save as..." or "Download"\n\nOr use "View in Browser" then ⌘+P → "Save as PDF"`);
  };

  const handleEmail = async (email = null) => {
    setEmailing(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${apiUrl}/api/submissions/${id}/email-report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: email || null })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Email failed');
      }
      
      alert(data.message || 'Report has been sent!');
      setEmailDialogOpen(false);
      setCustomEmail('');
    } catch (error) {
      console.error('Failed to email:', error);
      alert('Failed to send email. Please try again.');
    } finally {
      setEmailing(false);
    }
  };

  const getSourceIcon = (type) => {
    switch (type) {
      case 'web': return Globe;
      case 'internal': return Database;
      case 'publication': return BookOpen;
      default: return Globe;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!submission) {
    return (
      <Layout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 mx-auto text-destructive mb-4" />
          <h2 className="font-serif text-2xl font-bold mb-2">Submission Not Found</h2>
          <p className="text-muted-foreground mb-4">The requested submission could not be found.</p>
          <Link to="/history">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to History
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const isProcessing = submission.status === 'processing' || submission.status === 'pending';
  const isCompleted = submission.status === 'completed';

  return (
    <Layout>
      <div className="space-y-6" data-testid="submission-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <Link 
              to="/history" 
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
              data-testid="back-link"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to History
            </Link>
            <h1 className="font-serif text-3xl font-bold text-foreground">{submission.title}</h1>
            <div className="flex items-center gap-3 mt-2">
              <Badge className={getStatusColor(submission.status)} variant="secondary">
                {isProcessing && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                {submission.status}
              </Badge>
              {submission.risk_level && (
                <Badge className={getRiskColor(submission.risk_level)} variant="secondary">
                  {submission.risk_level} risk
                </Badge>
              )}
              {submission.analysis_details?.resubmission_of && (
                <Badge
                  variant="secondary"
                  className="bg-blue-50 text-blue-700 border-blue-200"
                  title="This file was previously submitted — the score from the earlier analysis was carried over for consistency"
                  data-testid="resubmission-badge"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Resubmission — score carried over
                </Badge>
              )}
              <span className="text-sm text-muted-foreground capitalize">
                {submission.submission_type} submission
              </span>
            </div>
          </div>
          
          {isCompleted && (
            <div className="flex flex-col gap-3">
              <Link to={`/submission/${id}/highlighted`}>
                <Button variant="default" className="w-full" data-testid="view-highlighted-report-btn">
                  <Layers className="w-4 h-4 mr-2" />
                  View Highlighted Report
                </Button>
              </Link>
              <Button 
                variant="outline"
                onClick={() => {
                  const token = localStorage.getItem('token');
                  const apiUrl = process.env.REACT_APP_BACKEND_URL;
                  window.open(`${apiUrl}/api/submissions/${id}/view-report?token=${token}`, '_blank');
                }}
                data-testid="download-report-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Get PDF Report
              </Button>
              <p className="text-xs text-muted-foreground">
                Opens report in new tab. Press <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Cmd+P</kbd> then "Save as PDF" to download.
              </p>
            </div>
          )}
        </div>

        {/* Processing State */}
        {isProcessing && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="py-8 text-center">
              <Loader2 className="w-12 h-12 animate-spin mx-auto text-blue-600 mb-4" />
              <h3 className="font-semibold text-lg text-blue-900 mb-2">Analyzing Your Document</h3>
              <p className="text-blue-700 mb-4">
                Your document is being checked against billions of sources. This usually takes 1-3 minutes.
              </p>
              <Button variant="outline" onClick={fetchData} className="border-blue-300">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Status
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Completed Results */}
        {isCompleted && (
          <>
            {/* Score Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-6 text-center">
                  <p className="text-sm text-muted-foreground mb-2">Similarity Score</p>
                  <p 
                    className={`font-mono text-5xl font-bold ${
                      submission.risk_level === 'high' ? 'text-red-600' :
                      submission.risk_level === 'moderate' ? 'text-amber-600' : 'text-emerald-600'
                    }`}
                  >
                    {submission.similarity_score?.toFixed(1)}%
                  </p>
                  <Progress 
                    value={submission.similarity_score || 0} 
                    className="h-2 mt-4"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Percentage of text matching known sources
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <p className="text-sm text-muted-foreground mb-2">AI Content Score</p>
                  <p className="font-mono text-5xl font-bold text-purple-600">
                    {submission.ai_score?.toFixed(1)}%
                  </p>
                  <Progress 
                    value={submission.ai_score || 0} 
                    className="h-2 mt-4"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Likelihood content was AI-generated
                  </p>
                  {submission.analysis_details?.ai_detection_method && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Detection: {submission.analysis_details.ai_detection_method === 'ensemble' ? 'GPT-4o + Heuristics' : submission.analysis_details.ai_detection_method}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* AI Indicators */}
            {submission.ai_indicators && submission.ai_indicators.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="font-serif text-base">AI Detection Indicators</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {submission.ai_indicators.map((indicator, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                        {indicator}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Matched Sources */}
            <Card>
              <CardHeader>
                <CardTitle className="font-serif">Matched Sources</CardTitle>
                <CardDescription>
                  {results.length} source{results.length !== 1 ? 's' : ''} found matching this document
                </CardDescription>
              </CardHeader>
              <CardContent>
                {results.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 mx-auto text-emerald-600 mb-4" />
                    <h3 className="font-semibold text-foreground mb-2">No Matches Found</h3>
                    <p className="text-muted-foreground">
                      Your document appears to be original with no significant matches detected.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {results.map((result, index) => {
                      const Icon = getSourceIcon(result.source_type);
                      return (
                        <div 
                          key={result.id || index}
                          className="flex items-start gap-4 p-4 bg-muted/30 rounded-sm"
                          data-testid={`source-${index}`}
                        >
                          <div className="w-10 h-10 bg-muted rounded-sm flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <h4 className="font-medium text-foreground">{result.source_title}</h4>
                                {result.source_url && (
                                  <a 
                                    href={result.source_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-accent hover:underline inline-flex items-center gap-1"
                                  >
                                    {result.source_url.slice(0, 50)}...
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                              </div>
                              <Badge variant="outline" className="flex-shrink-0">
                                {result.percentage_matched?.toFixed(1)}%
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                              <span className="capitalize">{result.source_type}</span>
                              <span>{result.matched_words} matched words</span>
                              <span className="capitalize">{result.match_type?.replace('_', ' ')}</span>
                            </div>
                            {result.matched_text && (
                              <div className="mt-3 p-3 bg-accent/10 border-l-2 border-accent rounded-r-sm">
                                <p className="text-sm text-foreground italic">"{result.matched_text}"</p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Document Details */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif">Document Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">File Name</p>
                  <p className="font-medium text-foreground">{submission.file_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">File Size</p>
                  <p className="font-medium text-foreground">{formatFileSize(submission.file_size)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Course / Module</p>
                  <p className="font-medium text-foreground">{submission.course || 'Not specified'}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Submitted</p>
                  <p className="font-medium text-foreground">{formatDate(submission.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="font-medium text-foreground">
                    {submission.completed_at ? formatDate(submission.completed_at) : 'In progress'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Document Hash</p>
                  <p className="font-mono text-sm text-foreground">{submission.file_hash?.slice(0, 32)}...</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <div className="bg-muted/50 border border-border rounded-sm p-4">
          <h4 className="font-medium text-foreground mb-2">Important Notice</h4>
          <p className="text-sm text-muted-foreground">
            This similarity report is provided as a tool to assist in identifying potential instances of textual similarity. 
            A high similarity score does not necessarily indicate plagiarism, as matching text may include properly cited 
            quotations, common phrases, or coincidental similarities. Academic integrity decisions should be made by 
            qualified faculty members after careful review.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default SubmissionDetail;
